<template>
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <h2 class="h2 card-header ">{{ $t('Register') }}</h2>
          <div class="card-body">
            <form>
              <div class="text-danger" v-if="errors.message">{{ errors.message }}</div>
              <div class="row justify-content-center">
                <base-input
                  class="form-group col-md-6"
                  v-model.trim="formData.firstName"
                  type="text"
                  :placeholder="$t('First Name')"
                  :error="errors.firstNameError"/>
              </div>
              <div class="row justify-content-center">
                <base-input
                  class="form-group col-md-6"
                  v-model.trim="formData.lastName"
                  type="text"
                  :placeholder="$t('Last Name')"
                  :error="errors.lastNameError"/>
              </div>
              <div class="row justify-content-center">
                <custom-form-component class="col-md-6" ref="customForm"></custom-form-component>
              </div>
              <div class="row justify-content-center">
                <base-input class="form-group col-md-6" v-model.trim="formData.email" type="email"
                            autocomplete="username"
                            placeholder="Email" :error="errors.emailError"></base-input>
              </div>
              <div class="row justify-content-center">
                <base-input
                  class="form-group col-md-6"
                  v-model.trim="formData.password"
                  type="password"
                  isPassword="true"
                  :placeholder="$t('Password')"
                  autocomplete="new-password"
                  :error="errors.passwordError"/>
              </div>
              <div class="row justify-content-center">
                <base-input
                  class="form-group col-md-6"
                  v-model.trim="formData.passwordConfirmation"
                  type="password" isPassword="true"
                  :placeholder="$t('Confirm Password')"
                  autocomplete="current-password"
                  :error="errors.passwordConfirmationError"
                  @enter="registration"
                />
              </div>
              <div class="row justify-content-center mt-3">
                <terms-checkbox
                  name="terms"
                  @checkbox:change="onCheckboxUpdate"
                  class="col-md-6 text-left"
                  :error="errors.terms"
                />
              </div>
              <div class="row justify-content-center">
                <news-checkbox
                  name="news"
                  @checkbox:change="onCheckboxUpdate"
                  class="col-md-6 text-left"
                />
              </div>
            </form>
            <div class="row justify-content-center">
              <loader-component v-if="loading"/>
              <captured-submit v-else :action="$options.AUTH_TYPE.REGISTRATION">
                <template v-slot:default="{execute}">
                  <button-component
                    class="btn registration-btn m-4"
                    @btn-click="execute(registration)"
                    @disabled-click="checkTerms"
                    :disabled="!formData.terms"
                  >
                    {{ $t('Register') }}
                  </button-component>
                </template>
              </captured-submit>
            </div>
            <social-auth
              :type="$options.AUTH_TYPE.REGISTRATION"
              :disabled="!formData.terms"
              :terms="formData.terms"
              :news="formData.news"/>
            <div class="button-colors">
              <p> {{ $t('If you have already registered, please') }} </p>
              <app-link
                class="link"
                :to="$options.PAGES.LOGIN"
              >
                {{ $t('click here to login') }}
              </app-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import BaseInput from '../components/base-components/BaseInput';
import {mapActions, mapGetters, mapState} from 'vuex';
import {AUTH_TYPE, ERROR, USER_CAPABILITY} from '@/const';
import CustomFormComponent from '../components/CustomFormComponent';
import LoaderComponent from '../components/LoaderComponent';
import SocialAuth from '../components/base-components/SocialAuth';
import TermsCheckbox from '../components/base-components/checkboxes/TermsCheckbox';
import NewsCheckbox from '../components/base-components/checkboxes/NewsCheckbox';
import ButtonComponent from '../components/base-components/buttons/ButtonComponent';
import CapturedSubmit from '../components/base-components/buttons/CapturedSubmit';
import {DO_REGISTRATION} from '@/store/actionTypes';
import {validateEmail} from '@/utils/helpers';
import AppLink from '@/components/base-components/AppLink';
import {HOME, LOGIN} from '@/pages';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import {GET_BASIC_PASS_SELECTED} from '@/store/getterTypes';

const ERRORS_FIELDS_NAME_MAP = {
  email: 'emailError',
  password: 'passwordError',
  passwordConfirmation: 'passwordConfirmationError',
  first_name: 'firstNameError',
  last_name: 'lastNameError',
};
export default {
  name: 'RegistrationScreen',
  components: {
    AppLink,
    CapturedSubmit,
    ButtonComponent,
    NewsCheckbox,
    TermsCheckbox,
    SocialAuth,
    LoaderComponent,
    CustomFormComponent,
    BaseInput,
  },
  PAGES: {LOGIN, HOME},
  AUTH_TYPE,
  data() {
    return {
      loading: false,
      additionalParamsToErrormessage: null,
      formData: {
        email: '',
        password: '',
        passwordConfirmation: '',
        firstName: '',
        lastName: '',
        news: false,
        terms: false,
      },
      errors: {
        emailError: '',
        passwordError: '',
        passwordConfirmationError: '',
        firstNameError: '',
        lastNameError: '',
        terms: '',
        message: '',
      },
    };
  },
  computed: {
    ...mapState({
      capabilities: (state) => state.auth.capabilities,
    }),
    ...mapGetters({
      isBasicPassSelected: GET_BASIC_PASS_SELECTED,
    }),
  },
  methods: {
    ...mapActions({doRegistration: DO_REGISTRATION}),
    checkTerms() {
      if (this.formData.terms) return true;
      this.errors.terms = 'Please accept terms & conditions';
    },
    redirectToLastPage(silent) {
      if (silent) {
        // this will act's like location.reload after href change.
        // It will help to move to previous location and reload page
        // to remove adSense scripts from there avoiding
        // trigger and immediately aborting several requests
        const path = this.$storage.get('last_page') || this.$options.PAGES.HOME.path;
        const url = new URL(path, location.origin);

        if (this.$storage.get('last_page') && this.$storage.get('last_page_query')) {
          Object.entries(this.$storage.get('last_page_query')).forEach(([key, val = '']) => {
            url.searchParams.append(key, val);
          });
        }
        location.href = url.href;
        return;
      }
      this.$storage.get('last_page') ?
        this.$router.replace({
          path: this.$storage.get('last_page'),
          query: this.$storage.get('last_page_query'),
        }) :
        this.$router.push(this.$options.PAGES.HOME);
    },
    onSuccessRegistration() {
      VisaicV3Analytics.event({
        event: 'user_register',
      });
      this.clearErrors();
      if (this.isBasicPassSelected) {
        this.redirectToLastPage(this.capabilities[USER_CAPABILITY.no_ads]);
      }
      this.loading = false;
    },
    registration({token} = {}) {
      if (this.loading) return;

      this.clearErrors();
      if (!this.formData.email ||
        this.formData.password.length < 8 ||
        this.formData.passwordConfirmation.length < 8 ||
        !this.formData.firstName.length ||
        !this.formData.lastName.length
      ) {
        this.loading = false;
        !this.formData.email ? this.errors.emailError = ERROR.ENTER_EMAIL : null;
        !this.formData.firstName.length ?
          this.errors.firstNameError = ERROR.ENTER_FIRST_NAME : null;
        !this.formData.lastName.length ? this.errors.lastNameError = ERROR.ENTER_LAST_NAME : null;
        this.formData.password.length < 8 ? this.errors.passwordError = ERROR.SHORT_PASSWORD : null;
        this.formData.passwordConfirmation.length < 8 ?
          this.errors.passwordConfirmationError = ERROR.SHORT_PASSWORD : null;
        return;
      } else if (this.formData.password !== this.formData.passwordConfirmation) {
        this.loading = false;
        this.errors.passwordError = ERROR.PASS_DONT_MATCH;
        this.errors.passwordConfirmationError = ERROR.PASS_DONT_MATCH;
        return;
      }
      if (!validateEmail(this.formData.email)) {
        this.errors.emailError = ERROR.WRONG_EMAIL;
        return;
      }
      if (!this.validateCustomInputs()) return;

      this.loading = true;
      const data = {
        email: this.formData.email,
        password: this.formData.password,
        password_confirmation: this.formData.passwordConfirmation,
        last_name: this.formData.lastName,
        first_name: this.formData.firstName,
        news: this.formData.news,
        terms: this.formData.terms,
        token,
      };
      const customFields = Object.entries(this.$refs.customForm.getFieldsData()).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {});

      if (customFields.phone) {
        customFields.phone = customFields.phone.replace(/\D/g, '');
        if (!customFields.phone.length) {
          delete customFields.phone;
        }
      }
      Object.assign(data, customFields);
      this.doRegistration(data).then(this.onSuccessRegistration).catch((data) => {
        if (data?.message) {
          if (data.message === ERROR.ALREADY_LOGGED) {
            this.errors.passwordError = ERROR.ALREADY_LOGGED;
            this.onSuccessRegistration();
          }
          if (data.message === ERROR.EMAIL_ALREADY_EXIST) {
            this.errors.emailError = ERROR.EMAIL_EXIST;
          }
        } else if (data?.errors) {
          Object.entries(data.errors).forEach(([key, value]) => {
            if (ERRORS_FIELDS_NAME_MAP[key]) {
              this.errors[ERRORS_FIELDS_NAME_MAP[key]] =
                value?.map?.(({message} = {}) => message)?.join('; ');
            }
          });
        }
        this.errors.message = data?.message || ERROR.SOMETHING_WRONG;

        this.loading = false;
      });
    },
    clearErrors() {
      this.errors.emailError = '';
      this.errors.passwordError = '';
      this.errors.passwordConfirmationError = '';
      this.errors.firstNameError = '';
      this.errors.lastNameError = '';
      this.errors.message = '';
    },
    validateCustomInputs() {
      return this.$refs.customForm.validate();
    },
    onCheckboxUpdate({
      name,
      value,
    }) {
      this.formData[name] = value;
      this.errors[name] = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/css/colors";

.container {
  margin: 9.5rem auto;
}

.link {
  color: $link-accent;
}

.registration-btn {
  background-color: var(--settings-registration-button-background);
  color: var(--settings-registration-button-text-color);
  position: relative;
  z-index: 1;
  border-radius: .3rem;

  &:before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    content: '';
    background-color: var(--settings-registration-button-background);
    transition: all .2s linear;
    border-radius: .3rem;
  }

  &:hover {
    background-color: var(--settings-registration-button-background);
    color: var(--settings-registration-button-text-color);
    box-shadow: none;

    &:before {
      filter: blur(3px);
      opacity: .5;
      transform: translate(3px, 3px);
    }
  }
}
</style>
